import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { workService } from '../services/workService';
import { Goal } from '../types/work';
import DataTable from '../components/common/DataTable';
import { GoalCard } from '../components/work/GoalCard';
import { WorkStatusBadge } from '../components/work/WorkStatusBadge';
import { PriorityChip } from '../components/work/PriorityChip';
import { DueDateLabel } from '../components/work/DueDateLabel';
import { ViewToggle } from '../components/work/ViewToggle';
import { FilterPanel } from '../components/work/FilterPanel';
import UserBadge from '../components/common/UserBadge';
import { FiPlus, FiTarget, FiAlertCircle } from 'react-icons/fi';
import { GoalDetailPanel } from '../components/work/GoalDetailPanel';
import CreateWorkItemModal from '../components/work/CreateWorkItemModal';

const Goals: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [view, setView] = useState<'cards' | 'table'>('cards');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [panelGoalId, setPanelGoalId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data: goals = [], isLoading, isError, refetch } = useQuery(['goals'], () => workService.getGoals());

  const filtered = useMemo(() => {
    let result = goals;
    if (statusFilter.length) result = result.filter(g => statusFilter.includes(g.status));
    if (priorityFilter.length) result = result.filter(g => priorityFilter.includes(g.priority));
    return result;
  }, [goals, statusFilter, priorityFilter]);

  const activeFilterCount = statusFilter.length + priorityFilter.length;

  const toggleFilter = (arr: string[], val: string, setter: (v: string[]) => void) => {
    setter(arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);
  };

  const columns: ColumnDef<Goal, any>[] = [
    {
      accessorKey: 'title',
      header: 'Goal',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="text-lg">{row.original.icon || '🎯'}</span>
          <span className="font-medium">{row.original.title}</span>
        </div>
      ),
    },
    { accessorKey: 'status', header: 'Status', cell: ({ row }) => <WorkStatusBadge status={row.original.status} /> },
    { accessorKey: 'priority', header: 'Priority', cell: ({ row }) => <PriorityChip priority={row.original.priority} /> },
    { accessorKey: 'due_date', header: 'Due', cell: ({ row }) => <DueDateLabel date={row.original.due_date} /> },
    {
      accessorKey: 'project_count',
      header: 'Projects',
      cell: ({ row }) => <span className="text-sm text-gray-400">{row.original.project_count ?? 0}</span>,
    },
    { accessorKey: 'owner', header: 'Owner', cell: ({ row }) => <UserBadge owner={row.original.owner} size="sm" /> },
  ];

  return (
    <div className="space-y-4">
      {/* Modals */}
      {showCreate && <CreateWorkItemModal entityType="goal" onClose={() => setShowCreate(false)} />}
      {panelGoalId && <GoalDetailPanel goalId={panelGoalId} onClose={() => setPanelGoalId(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
            Goals
          </h1>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Strategic objectives driving your work
          </p>
        </div>
        <div className="flex items-center gap-3">
          <FilterPanel
            activeCount={activeFilterCount}
            onClear={() => { setStatusFilter([]); setPriorityFilter([]); }}
          >
            <div className="space-y-1">
              <label className="text-xs text-gray-400">Status</label>
              <div className="flex flex-wrap gap-1">
                {['draft', 'active', 'completed', 'cancelled', 'archived'].map(s => (
                  <button key={s} onClick={() => toggleFilter(statusFilter, s, setStatusFilter)}
                    className={`px-2 py-0.5 rounded text-xs ${statusFilter.includes(s) ? 'bg-emerald-900/40 text-emerald-300' : darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}
                  >{s}</button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-400">Priority</label>
              <div className="flex flex-wrap gap-1">
                {['critical', 'high', 'medium', 'low'].map(p => (
                  <button key={p} onClick={() => toggleFilter(priorityFilter, p, setPriorityFilter)}
                    className={`px-2 py-0.5 rounded text-xs ${priorityFilter.includes(p) ? 'bg-violet-900/40 text-violet-300' : darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}
                  >{p}</button>
                ))}
              </div>
            </div>
          </FilterPanel>
          <ViewToggle view={view} onChange={setView} />
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-500 text-white text-sm font-medium hover:shadow-lg hover:shadow-emerald-500/20 transition-shadow">
            <FiPlus className="w-3.5 h-3.5" /> New Goal
          </button>
        </div>
      </div>

      {/* Content */}
      {isError ? (
        <div className="flex flex-col items-center py-20">
          <FiAlertCircle className="w-12 h-12 text-red-400 mb-4" />
          <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Failed to load goals</h3>
          <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Check your connection and try again.</p>
          <button onClick={() => refetch()} className="px-4 py-2 rounded-lg bg-gray-700 text-white text-sm hover:bg-gray-600">Retry</button>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" /></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center py-20">
          <FiTarget className={`w-12 h-12 mb-4 ${darkMode ? 'text-emerald-400/40' : 'text-emerald-300'}`} />
          <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>No goals defined yet</h3>
          <p className={`text-sm mb-6 max-w-md text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Goals are the strategic destinations that give your projects direction.
          </p>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-500 text-white text-sm font-medium">
            Create your first goal
          </button>
        </div>
      ) : view === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(goal => (
            <GoalCard key={goal.id} goal={goal} onClick={() => setPanelGoalId(goal.id)} />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          onRowClick={(goal: Goal) => setPanelGoalId(goal.id)}
          searchable
          searchPlaceholder="Search goals..."
          emptyMessage="No goals match your filters"
        />
      )}
    </div>
  );
};

export default Goals;
